// Universal Fusion: API wrappers, vocabulary and formatting shared by every Fusion component.
// Backend: api/v2/fusion (Features:Fusion). Semantics: docs/architecture/FUSION.md. Every figure is "connected exposure" over distinct risks and is never summed across
// chains; "correlated" only ever names a confidence tier.

// F4: the scopes, modules and wording of axaris_fusion_standalone.html (user decision 2026-10-03).
export const SCOPES = [
  { key: "Organization", label: "Organization" },
  { key: "Identity", label: "Identity", spine: true },
  { key: "Assets", label: "Assets", spine: true },
  { key: "Enterprise", label: "Enterprise" },
  { key: "BusinessUnit", label: "Business unit" },
  { key: "Deal", label: "Deal" },
  { key: "Ransomware", label: "Ransomware" },
  { key: "DarkWeb", label: "Dark Web" },
  { key: "AI", label: "AI" },
  { key: "TPRM", label: "TPRM" },
  { key: "GRC", label: "GRC" },
  { key: "External", label: "External" },
  { key: "Application", label: "Application" },
];

// Module colours follow the sample: identity/asset grey, dark web red, AI purple, ransomware coral,
// TPRM amber, GRC blue, business unit teal.
export const LANES = {
  Identity: { label: "Identity", color: "#5F5E5A" },
  Device: { label: "Asset", color: "#888780" },
  DarkWeb: { label: "Dark Web", color: "#E24B4A" },
  AI: { label: "AI", color: "#7F77DD" },
  Ransomware: { label: "Ransomware", color: "#D85A30" },
  TPRM: { label: "TPRM", color: "#EF9F27" },
  GRC: { label: "GRC", color: "#378ADD" },
  Enterprise: { label: "Enterprise", color: "#0E7490" },
  DefenderXdr: { label: "Defender XDR", color: "#B45309" },
  External: { label: "External", color: "#64748B" },
  BusinessUnit: { label: "Business unit", color: "#1D9E75" },
  Deal: { label: "Deal", color: "#4F46E5" },
};

export const laneLabel = (lane) => LANES[lane]?.label || lane;
export const laneColor = (lane) => LANES[lane]?.color || "#6b7280";

export const SORTS = [
  { key: "severity", label: "Severity" },
  { key: "exposure", label: "Exposure $" },
  { key: "leverage", label: "Chains broken" },
  { key: "confidence", label: "Confidence" },
];

export const SEVERITIES = ["Critical", "High", "Medium", "Low"];

export const OBJECT_TYPE_LABEL = {
  Identity: "Identities",
  Device: "Assets",
  AiAsset: "AI assets",
  Control: "GRC controls",
  Vendor: "Vendors",
  BusinessUnit: "Business units",
  Deal: "Deals",
};

export const JOIN_LABEL = {
  RiskIdentity: "Findings linked to an account",
  RiskDevice: "Findings linked to a device",
  IdentityDevice: "Accounts with device sign-ins",
  IdentityAiAgent: "AI agents linked to their account",
  RiskAiAsset: "Findings on an AI asset",
  ControlIdentity: "Controls naming specific accounts",
  ControlRisk: "Findings from a control",
  RiskVendor: "Findings linked to a vendor",
  AiAssetAccountableTo: "AI agent's accountable person",
};

// Empty-state coverage table (FUSION.md §7.2). Copy user-approved 2026-10-29.
export const COVERAGE_CAPTION = "Where links come from";
export const COVERAGE_TYPE_LABEL = {
  RiskAffectsIdentity: "Findings linked to an account",
  RiskAffectsDevice: "Findings linked to a device",
  IdentitySignedInTo: "Accounts with device sign-ins",
  AiAssetRunsAs: "AI agents linked to their account",
  ControlAffectsIdentity: "Controls naming specific accounts",
  RiskFromControl: "Findings from a control",
  RiskOnAiAsset: "Findings on an AI asset",
  RiskConcernsVendor: "Findings linked to a vendor",
  AiAssetAccountableTo: "AI agents and their owners",
};
export const COVERAGE_STATE_LABEL = {
  Populated: "Links found",
  AvailableEmpty: "Checked, no links found",
  Failing: "Last sync failed",
  NotConfigured: "Not set up",
  Unavailable: "Not available",
};
export const COVERAGE_DEGRADED_LABEL = "Links found, some syncs failing";
export const COVERAGE_REASON_LABEL = {
  NoDeviceLogonEventsTable: "needs Microsoft Defender for Endpoint",
  PermissionDenied: "a permission is missing",
  ConnectorOff: "the connection is turned off",
  CapabilityMissing: "not available for this tenant",
  LegacyEntityTables: "not available in this installation",
};

const own = (map, key) => typeof key === "string" && Object.prototype.hasOwnProperty.call(map, key);

export const coverageText = (row) => {
  const state = row && row.state;
  if (state === "Populated" && row.degraded === true) return COVERAGE_DEGRADED_LABEL;
  if (!own(COVERAGE_STATE_LABEL, state)) return "Unknown";
  if (state === "Unavailable" && own(COVERAGE_REASON_LABEL, row.reasonCode)) {
    return "Not available: " + COVERAGE_REASON_LABEL[row.reasonCode];
  }
  return COVERAGE_STATE_LABEL[state];
};

// Exact on-screen wording. Partial view and the non-additive note are security / accuracy rules
// from the plan; the rest is the sample's wording (user decision 2026-10-03).
export const PARTIAL_VIEW_TEXT = "Some connected objects are not visible with your current access.";
export const NON_ADDITIVE_TEXT = "Chain exposure values are calculated independently and should not be summed.";

// C2 copy, user-approved (PLAN "Copy: Set A"). Every Fusion view reads these; never inline them.
export const DEMO_TITLE = "Demonstration data";
export const DEMO_TEXT = "This organization shows example records, not results from your connected tools.";
export const SCENARIO_LABEL = "If this chain is exploited";
export const SCENARIO_NOTE = "Scenario estimate from proposed factors. Not included in the correlated exposure.";
// Walkthrough 2026-10-26: the three finance figures, always shown apart (the FAIR figure is never replaced).
export const EXPECTED_LABEL = "Expected annual loss (FAIR)";
export const EXPECTED_HINT = "Each risk at its own likelihood.";
export const CHAIN_EFFECT_LABEL = "Chain effect (estimate)";
export const CHAIN_EFFECT_HINT = "Extra loss because these risks make each other more likely. Not included in the expected annual loss; the factors are proposals.";
export const EXPLOITED_HINT = "Expected annual loss plus the chain effect.";
export const FORECASTS_HEADING = "From the individual risks";
// CEO decision 2026-10-27: the chain's break-point is labelled "Break Point" (was "Resolve first").
export const BREAK_POINT = "Break Point";
export const BREAK_POINT_HINT = "Fix this first: it breaks the most chains for the least effort.";
export const CONSEQUENCE_HEADING = "How this chain plays out";
export const NO_STATEMENTS_TEXT = "Connected through shared records; no consequence pattern recognised yet.";

// The C1 consequence categories, in FusionConsequenceCategories.All order (FUSION.md §13).
export const CONSEQUENCES = [
  { key: "AccountTakeover", label: "Account takeover" },
  { key: "DeviceFoothold", label: "Device foothold" },
  { key: "AgentMisuse", label: "AI agent misuse" },
  { key: "DataExposure", label: "Data exposure" },
  { key: "ThirdParty", label: "Third-party" },
];

/** True only for an exact (case-sensitive) CONSEQUENCES key. */
export const isConsequenceKey = (v) => CONSEQUENCES.some((c) => c.key === v);

export const consequenceLabel = (key) => CONSEQUENCES.find((c) => c.key === key)?.label || key;

/** A break-point's effort (FUSION.md §4.6.4) as Low / Medium / High, marked "(proposed)" while its factors are proposals. */
export const effortLabel = (effort, isProposal) => {
  if (effort === null || effort === undefined) return null;
  const label = effort <= 1 ? "Low" : effort <= 2 ? "Medium" : "High";
  return isProposal ? `${label} (proposed)` : label;
};

/** SVG strokeDasharray for an arrow by tier: confirmed solid, correlated/declared dashed, everything else dotted. */
export const tierStroke = (tier) =>
  tier === "confirmed" ? undefined
    : tier === "correlated" || tier === "declared" ? "6 3"
    : "1 3";

export const tierLabel = (tier) =>
  tier === "confirmed" ? "Confirmed"
    : tier === "correlated" ? "Correlated (not confirmed)"
    : tier === "declared" ? "Declared"
    : tier === "inferred" ? "Inferred"
    : tier === "aggregated" ? "Aggregated"
    : "Not resolved";

// What a link matched on, in words (a link's MatchKind). Unknown kinds fall back to the tier label.
const MATCH_KIND_TEXT = {
  ObjectId: "directory ID", Sid: "account SID", Upn: "user principal name", Email: "email",
  AccountName: "account name", DomainAccount: "account name", DnsName: "host name",
  MdeDeviceId: "device ID", AadDeviceId: "device ID", AiAssetId: "AI asset record",
  AiDataLeakAssetLinker: "app name", VendorId: "vendor record", VendorMatch: "vendor domain",
  EnzoicDomain: "breached domain", EsmDomain: "monitored domain", ThreatEventDomain: "leak-site domain",
  RepoTarget: "repository", ApplicationVendor: "application vendor", IdentityInventoryId: "account record",
  SecurityControlId: "control record", ManagedIdentity: "managed identity",
};

/**
 * An arrow's confidence in plain words, from its tier and the weakest link's match kind:
 * "Confirmed", or "Matched by email (not confirmed)".
 */
export const matchLabel = (tier, links) => {
  if (tier !== "correlated") return tierLabel(tier);
  const weakest = (links || []).find((l) => l.confidence === "correlated");
  const what = weakest && MATCH_KIND_TEXT[weakest.matchKind];
  return what ? `Matched by ${what} (not confirmed)` : tierLabel(tier);
};

/** Inferred/aggregated context chips are drawn dashed, so they are never mistaken for proof. */
export const isWeakTier = (tier) => tier === "aggregated" || tier === "inferred";

// Same format as the backend's FusionConsequenceRules.FormatMoney: a figure that rounds up to the next
// unit is shown in it ($1.00M, never $1000K; $1.00B, never $1000.00M; $1K, never $1000).
export const formatMoney = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const v = Number(value);
  const a = Math.abs(v);
  const billions = () => `$${(v / 1e9).toFixed(2)}B`;
  const millions = () => (Math.abs(Number((v / 1e6).toFixed(2))) >= 1000 ? billions() : `$${(v / 1e6).toFixed(2)}M`);
  if (a >= 1e9) return billions();
  if (a >= 1e6) return millions();
  if (a >= 1e3) return Math.abs(Math.round(v / 1e3)) >= 1000 ? millions() : `$${Math.round(v / 1e3)}K`;
  return Math.abs(Math.round(v)) >= 1000 ? "$1K" : `$${Math.round(v)}`;
};

export const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

export const severityClass = (sev) => {
  switch ((sev || "").toLowerCase()) {
    case "critical": return "bg-red-100 text-red-700";
    case "high": return "bg-orange-100 text-orange-700";
    case "medium": return "bg-yellow-100 text-yellow-800";
    case "low": return "bg-green-100 text-green-700";
    default: return "bg-gray-100 text-gray-600";
  }
};

/** Key of a node, the same spelling the API uses (Type:Id). */
export const nodeKey = (type, id) => `${type}:${id}`;

/**
 * A Fusion risk member's RiskType (the unified name) as the frontend risk type the existing flows
 * expect — e.g. the assignment activity log — mirroring UnifiedRiskTypes.UnifiedToFrontend.
 */
export const frontendRiskTypeOf = (riskType) =>
  riskType === "DealApplication" ? "Deals"
    : riskType === "BusinessUnit" ? "BusinessUnits"
    : riskType || null;

/** The remediation "type" the existing Jira / ServiceNow modals route on, from a Fusion risk member. */
export const remediationTypeOf = (riskType) => (riskType === "GRC" ? "grc" : "external");

// ── API ─────────────────────────────────────────────────────────────────────────────────────────

const qs = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

export const fetchSummary = (apiCall, { refresh = false } = {}) =>
  apiCall("GET", `/fusion/summary?${qs({ refresh: refresh || undefined })}`);

export const fetchChains = (apiCall, { scope, bucket, severity, consequence, sort, skip, take, refresh }) =>
  apiCall("GET", `/fusion/chains?${qs({ scope, bucket, severity, consequence, sort, skip, take, refresh: refresh || undefined })}`);

export const fetchChain = (apiCall, { anchorType, anchorId, refresh }) =>
  apiCall("GET", `/fusion/chain?${qs({ anchorType, anchorId, refresh: refresh || undefined })}`);

export const fetchWhatIf = (apiCall, { anchorType, anchorId, nodeType, nodeId }) =>
  apiCall("GET", `/fusion/chain/whatif?${qs({ anchorType, anchorId, nodeType, nodeId })}`);

// ── F3: tracked chains (full-access users only; changes need RISK_MANAGER+ at ORG) ────────────────

export const fetchTracked = (apiCall) => apiCall("GET", "/fusion/tracked");

export const trackChain = (apiCall, { anchorType, anchorId }) => apiCall("POST", "/fusion/tracked", { anchorType, anchorId });

export const untrackChain = (apiCall, id) => apiCall("DELETE", `/fusion/tracked/${encodeURIComponent(id)}`);

export const fetchTrackedHistory = (apiCall, id, days = 90) =>
  apiCall("GET", `/fusion/tracked/${encodeURIComponent(id)}/history?days=${days}`);
